import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { ROUTES } from '../../constants/routes';
import { ACCESS } from '../../constants/authz';
import { authzContextAtom } from '../../authorization/authzState';
import { canAccessAnyBusinessFunction, canAccessResource } from '../../authorization/canAccessResource';
import useOrganisationAccounts from '../../features/business-functions/hooks/useOrganisationAccounts';
import { canCreateBusinessFunction } from '../../features/business-functions/utils/canCreateBusinessFunction';
import { userInformationAtom } from '../../Services/_states';

function hasBusinessFunctionsModuleAccess(authz) {
  if (!authz?.isAuthzV2Enabled || authz?.isFullAccess) {
    return true;
  }
  return canAccessAnyBusinessFunction(authz);
}

/**
 * Business Functions sidebar: one entry per accessible business function.
 * Hidden for platform super admins.
 */
export default function useBusinessFunctionsNavItems() {
  const userInfo = useRecoilValue(userInformationAtom);
  const authz = useRecoilValue(authzContextAtom);
  const organisationId =
    userInfo?.profile?.organisation?.organisation_id ||
    userInfo?.profile?.organisation_id ||
    '';

  const { accounts, isLoading } = useOrganisationAccounts(organisationId);
  const isSuperAdmin = canCreateBusinessFunction(userInfo);

  const items = useMemo(() => {
    if (isSuperAdmin || !organisationId || !hasBusinessFunctionsModuleAccess(authz)) {
      return [];
    }

    if (isLoading) {
      return [];
    }

    return (accounts || [])
      .filter((account) => account?.account_id && account?.is_active !== false)
      .filter((account) => {
        if (!authz.isAuthzV2Enabled || authz.isFullAccess) {
          return true;
        }
        return canAccessResource(authz, {
          type: 'account',
          id: account.account_id,
          action: 'view',
        });
      })
      .map((account) => {
        const name = account.name || account.account_name || 'Untitled account';
        return {
          title: name,
          key: `businessFunction:${account.account_id}`,
          iconKey: 'store',
          route: `${ROUTES.CAMPAIGN.BUSINESS_FUNCTIONS}/${encodeURIComponent(account.account_id)}?name=${encodeURIComponent(name)}`,
          access: ACCESS.BUSINESS_FUNCTIONS_ACCESS,
        };
      });
  }, [accounts, authz, isLoading, isSuperAdmin, organisationId]);

  return { items, organisationId, isLoading };
}
